import { CalendarCheck, Truck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { TiltCard } from "@/components/landing/TiltCard";
import { scrollToId, PHONE, PHONE_TEL } from "@/lib/site";

const STEPS = [
  {
    n: "01",
    icon: CalendarCheck,
    title: "Book in 30 seconds",
    text: `Schedule online or call ${PHONE}. Pick your day and time window — that's it.`,
    img: "/images/google/g3.webp",
    fallback: "/images/google/g3.jpg",
  },
  {
    n: "02",
    icon: Truck,
    title: "We pick up at your door",
    text: "Our driver grabs your bag right from your doorstep — no trip to the strip mall.",
    img: "/images/deliverguy.jpg",
    fallback: null,
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Back fresh in ~48 hours",
    text: "Cleaned, pressed, and delivered back to your door. Free delivery, every time.",
    img: "/images/google/g6.webp",
    fallback: "/images/google/g6.jpg",
  },
];

export const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works" className="bg-white py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal className="mx-auto max-w-2xl text-center">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue sm:text-sm">How it works</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
          Three steps. Zero errands.
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {STEPS.map((step, i) => (
          <Reveal key={step.n} delay={i * 0.12}>
            <TiltCard>
              <div className="group relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 transition-shadow hover:shadow-xl hover:shadow-brand-blue/10">
                {step.img && (
                  <img
                    src={step.img}
                    onError={step.fallback ? (e) => (e.currentTarget.src = step.fallback) : undefined}
                    alt={step.title}
                    loading="lazy"
                    decoding="async"
                    className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                <div className="p-8">
                  <div className="flex items-center justify-between">
                    <span className="text-outline-navy font-display text-5xl font-extrabold">{step.n}</span>
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue/10 text-brand-blue">
                      <step.icon className="h-6 w-6" />
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-brand-navy sm:text-xl">{step.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-600">{step.text}</p>
                </div>
              </div>
            </TiltCard>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-12 text-center">
        <button
          data-testid="how-it-works-book-button"
          onClick={() => scrollToId("book")}
          className="rounded-full bg-brand-blue px-8 py-4 font-display text-sm font-extrabold uppercase tracking-widest text-white shadow-lg shadow-brand-blue/30 transition-transform hover:scale-105"
        >
          Book my free pickup
        </button>
        <p className="mt-4 text-sm text-slate-500">
          Prefer to talk?{" "}
          <a href={PHONE_TEL} className="font-bold text-brand-blue hover:underline">
            Call {PHONE}
          </a>
        </p>
      </Reveal>
    </div>
  </section>
);
